import React, { createContext, useContext, useState, useEffect } from 'react';
import { waitlistService } from '../services/waitlistService';


const WaitlistContext = createContext();

export const useWaitlist = () => {
  const context = useContext(WaitlistContext);
  if (!context) {
    throw new Error('useWaitlist must be used within a WaitlistProvider');
  }
  return context;
};

export const WaitlistProvider = ({ children }) => {
  const [waitlistCount, setWaitlistCount] = useState(0);
  const [spotsRemaining, setSpotsRemaining] = useState(1000);
  const [loading, setLoading] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [error, setError] = useState(null);

  const refreshWaitlistCount = async () => {
    try {
      setLoading(true);
      setError(null);
      const { count, spotsRemaining } = await waitlistService?.getWaitlistCount();
      setWaitlistCount(count);
      setSpotsRemaining(spotsRemaining);
    } catch (err) {
      console.error('Failed to load waitlist count:', err);
      setError(err?.message);
      // Set fallback values if loading fails
      setWaitlistCount(0);
      setSpotsRemaining(1000);
    } finally {
      // CRITICAL: Always set loading to false to unblock buttons
      setLoading(false);
      console.log(' WaitlistContext: Loading state set to false');
    }
  };

  useEffect(() => {
    refreshWaitlistCount();
  }, []);

  const addToWaitlist = async (email) => {
    // ✅ DEBUG LOG: Context received request
    console.log('🔄 WaitlistContext.addToWaitlist called for:', email);
    
    setLoading(true);
    setError(null);
    try {
      // ✅ DEBUG LOG: Before calling service
      console.log('📤 Calling waitlistService.addEmail from context');
      const result = await waitlistService?.addEmail(email);
      
      // ✅ DEBUG LOG: Service response
      console.log('📥 Service response in context:', {
        success: result?.success,
        count: result?.count,
        spotsRemaining: result?.spotsRemaining,
        emailSent: result?.emailSent,
        emailError: result?.emailError,
      });
      
      if (result?.success) {
        // Update local state with the returned counts
        setWaitlistCount(result?.count);
        setSpotsRemaining(result?.spotsRemaining);
        
        // ✅ DEBUG LOG: State updated
        console.log('✅ Context state updated successfully');
        
        return { 
          success: true, 
          alreadyExists: result?.alreadyExists,
          message: result?.message,
          emailSent: result?.emailSent,
          emailError: result?.emailError,
        };
      }
      
      // ✅ DEBUG LOG: Unsuccessful result
      console.warn('⚠️ Service returned unsuccessful result:', result);
      return result;
    } catch (err) {
      console.error("❌ WaitlistContext.addToWaitlist failed", err);
      setError("Something went wrong. Please try again.");
      return { success: false, error: err?.message };
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSuccessModal = () => {
    console.log('❌ WaitlistContext: Closing success modal');
    setShowSuccessModal(false);
  };

  const value = {
    waitlistCount,
    spotsRemaining,
    loading,
    addToWaitlist,
    showSuccessModal,
    handleCloseSuccessModal,
    setShowSuccessModal
  };

  return (
    <WaitlistContext.Provider value={value}>
      {children}
    </WaitlistContext.Provider>
  );
};